import { ReactNode } from 'react';
import classNames from 'classnames';

export interface Column<DataType extends Record<string, any>> {
  title: string;
  valueMapper?: (data: DataType, index: number) => ReactNode;
}

export interface SimpleTableProps<DataType extends Record<string, any>> {
  data: DataType[];
  columns: Column<DataType>[];
  containerClassName?: string;
}

export function SimpleTableHeaderCell({ title }: { title: string }) {
  return (
    <th
      scope="col"
      className="py-3.5 pl-2 text-left text-xs font-medium text-black"
    >
      {title.toUpperCase()}
    </th>
  );
}

export function SimpleTable<DataType extends Record<string, any>>({
  data,
  columns,
  containerClassName,
}: SimpleTableProps<DataType>) {
  return (
    <TableContainer className={containerClassName}>
      <TableHeader columns={columns} />
      <tbody className="divide-y divide-gray-200 bg-white">
        <TableRow data={data} columns={columns} />
      </tbody>
    </TableContainer>
  );
}

export function TableContainer(props: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={classNames('flex flex-col', props.className)}>
      <div className="-my-2 overflow-x-auto">
        <div className="inline-block min-w-full py-2 align-middle">
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-300 border-b">
              {props.children}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TableHeader<DataType extends Record<string, any>>(props: {
  columns: Column<DataType>[];
}) {
  return (
    <thead className="bg-gray-100">
      <tr>
        {props.columns.map((column) => (
          <SimpleTableHeaderCell key={column.title} title={column.title} />
        ))}
      </tr>
    </thead>
  );
}

export function TableRow<DataType extends Record<string, any>>(props: {
  data: DataType[];
  columns: Column<DataType>[];
}) {
  return (
    <>
      {props.data.map((dataEntry, index) => (
        <tr key={dataEntry.id ?? index}>
          {props.columns.map((column) => (
            <td className="whitespace-nowrap py-2 text-[0.8rem] text-gray-800 pl-2 first:align-top">
              {column.valueMapper
                ? column.valueMapper(dataEntry, index)
                : dataEntry[column.title.toLowerCase()]}
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
